/**
 * The deal page's left rail: the groups the deal's files fall into, with a
 * count each. Picking one filters the table; "All files" clears it.
 */
import { Trash2 } from "lucide-react";
import React from "react";

export interface FileGroup {
	key: string;
	label: string;
	count: number;
}

interface FileGroupNavProps {
	heading: string;
	total: number;
	groups: FileGroup[];
	selected: string | null;
	onSelect: (key: string | null) => void;
	/** Offered on the groups it returns true for (sub-folders). */
	canDelete?: (key: string) => boolean;
	onDelete?: (key: string) => void;
}

// The keyboard focus ring is drawn INSET: an outer ring on a stacked list is
// painted over by the hovered neighbour's background. Mouse clicks get none.
const itemClass = (on: boolean) =>
	`flex w-full items-center justify-between gap-2 rounded-[7px] px-2.5 py-[7px] text-left text-[13px] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#004C4B] ${
		on ? "bg-[#E6F3F3] font-semibold text-[#004C4B]" : "text-gray-700 hover:bg-gray-50"
	}`;

const FileGroupNav: React.FC<FileGroupNavProps> = ({
	heading,
	total,
	groups,
	selected,
	onSelect,
	canDelete,
	onDelete,
}) => (
	<nav aria-label={heading} className="flex flex-col gap-0.5">
		<div className="px-2.5 pb-1.5 text-[11px] font-semibold uppercase tracking-[0.09em] text-text-muted">
			{heading}
		</div>
		<button
			type="button"
			aria-pressed={selected === null}
			onClick={() => onSelect(null)}
			className={itemClass(selected === null)}
		>
			<span>All files</span>
			<span className="text-xs text-text-muted">{total}</span>
		</button>
		{groups.map((group) => {
			const deletable = Boolean(onDelete && canDelete?.(group.key));
			return (
				<div key={group.key} className="group relative">
					<button
						type="button"
						aria-pressed={selected === group.key}
						onClick={() => onSelect(group.key)}
						className={itemClass(selected === group.key)}
					>
						<span className="truncate">{group.label}</span>
						<span
							className={`text-xs text-text-muted ${
								deletable ? "group-focus-within:invisible group-hover:invisible" : ""
							}`}
						>
							{group.count}
						</span>
					</button>
					{deletable && (
						<button
							type="button"
							aria-label={`Delete folder ${group.label}`}
							title="Delete folder"
							onClick={() => onDelete?.(group.key)}
							className="absolute right-1 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-gray-400 opacity-0 hover:bg-red-50 hover:text-red-600 focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#004C4B] group-hover:opacity-100"
						>
							<Trash2 className="h-3.5 w-3.5" aria-hidden />
						</button>
					)}
				</div>
			);
		})}
	</nav>
);

export default FileGroupNav;
